import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Download, Loader2, Mail, Paperclip, X } from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';
import { useDailyReport } from '../../../hooks/useDailyStatus';
import {
  downloadReport,
  listReportHistory,
  sendReport,
  type ReportSection,
  type ReportTask,
} from '../../../lib/dailyStatusApi';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Undefined = the caller's own report. */
  userId?: string;
  date: string;
  canExportPdf: boolean;
  canSendEmail: boolean;
}

export const SECTION_ORDER: ReportSection[] = ['done', 'inProgress', 'blocked', 'notStarted'];

export const SECTION_STYLE: Record<ReportSection, { emoji: string; text: string; border: string }> =
  {
    done: { emoji: '✅', text: 'text-emerald-700', border: 'border-emerald-400' },
    inProgress: { emoji: '🔄', text: 'text-blue-700', border: 'border-blue-400' },
    blocked: { emoji: '🚫', text: 'text-red-700', border: 'border-red-400' },
    notStarted: { emoji: '⬜', text: 'text-gray-600', border: 'border-gray-300' },
  };

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function ReportTaskLine({ task }: { task: ReportTask }) {
  const { t } = useI18n();
  return (
    <li className="py-1.5 text-[13px]">
      <div className="flex flex-wrap items-baseline gap-x-2">
        {task.identifier && (
          <span className="font-mono text-[12px] text-gray-400">{task.identifier}</span>
        )}
        <span className="text-gray-800">{task.title}</span>
        {task.source === 'personal' && (
          <span className="rounded-full border border-amber-300 px-1.5 text-[10px] text-amber-700">
            Personal
          </span>
        )}
        {task.hoursToday > 0 && (
          <span className="text-[11px] tabular-nums text-gray-400">
            {t('dailyStatus.hours', { hours: task.hoursToday })}
          </span>
        )}
        {task.unreported && (
          <span className="text-[11px] italic text-amber-600">{t('dailyStatus.unreported')}</span>
        )}
      </div>
      {task.checkIn?.comment && (
        <p className="mt-0.5 whitespace-pre-wrap text-[12.5px] text-gray-600">
          {task.checkIn.comment}
        </p>
      )}
      {(task.checkIn?.attachments?.length ?? 0) > 0 && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {task.checkIn?.attachments.map((a, i) => (
            <a
              key={`${a.name}-${i}`}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-100"
            >
              <Paperclip size={10} /> {a.name}
            </a>
          ))}
        </div>
      )}
    </li>
  );
}

export function ReportSections({ sections }: { sections: Record<ReportSection, ReportTask[]> }) {
  const { t } = useI18n();
  return (
    <>
      {SECTION_ORDER.map((section) => {
        const tasks = sections[section];
        if (tasks.length === 0) return null;
        const style = SECTION_STYLE[section];
        return (
          <div key={section} className="mt-4">
            <p className={`text-xs font-bold uppercase tracking-wider ${style.text}`}>
              {style.emoji} {t(`dailyStatus.section.${section}`)} ({tasks.length})
            </p>
            <ul className={`mt-1 divide-y divide-gray-100 border-l-2 pl-3 ${style.border}`}>
              {tasks.map((task) => (
                <ReportTaskLine key={task.id} task={task} />
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

/**
 * One person's end-of-day report, in a side panel (desktop) / bottom sheet
 * (phone): the four sections, copy-as-markdown, PDF and "email it now", plus
 * the last two weeks of frozen snapshots to flip through.
 */
export function DailyReportDrawer({
  open,
  onClose,
  userId,
  date,
  canExportPdf,
  canSendEmail,
}: Props) {
  const { t, locale } = useI18n();
  const [day, setDay] = useState(date);
  useEffect(() => setDay(date), [date, open]);

  const report = useDailyReport(userId, day, open);
  const history = useQuery({
    queryKey: ['daily-status', 'history', userId ?? 'me'],
    queryFn: () => listReportHistory(isoDaysAgo(14), isoDaysAgo(0), userId),
    enabled: open,
    staleTime: 60_000,
  });

  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<'pdf' | 'email' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function copyMarkdown() {
    if (!report.data) return;
    try {
      await navigator.clipboard.writeText(report.data.markdown);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setNotice({ tone: 'error', text: t('dailyStatus.drawer.copyFailed') });
    }
  }

  async function downloadPdf() {
    setBusy('pdf');
    setNotice(null);
    try {
      await downloadReport('me', 'pdf', day, userId);
    } catch {
      setNotice({ tone: 'error', text: t('daily.exportFailed') });
    } finally {
      setBusy(null);
    }
  }

  async function email() {
    setBusy('email');
    setNotice(null);
    try {
      const res = await sendReport(userId, day);
      setNotice({ tone: 'ok', text: t('dailyStatus.drawer.sent', { to: res.to }) });
    } catch (e) {
      setNotice({
        tone: 'error',
        text: e instanceof Error ? e.message : t('daily.emailFailed'),
      });
    } finally {
      setBusy(null);
    }
  }

  const when = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  const dayLabel = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${day}T12:00:00Z`));
  const btn =
    'inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-[13px] font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-stretch sm:justify-end">
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/30"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('dailyStatus.drawer.title')}
        className="relative flex max-h-[92dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl sm:h-full sm:max-h-none sm:w-[520px] sm:rounded-none"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">
              {t('dailyStatus.drawer.title')}
            </h2>
            <p className="truncate text-xs text-gray-500">
              {report.data ? `${report.data.user.name} · ` : ''}
              {dayLabel}
              {report.data &&
                ` · ${
                  report.data.persisted
                    ? t('dailyStatus.drawer.persisted', {
                        time: when.format(new Date(report.data.generatedAt)),
                      })
                    : t('dailyStatus.drawer.live')
                }`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-2">
          <button type="button" onClick={copyMarkdown} disabled={!report.data} className={btn}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? t('dailyStatus.drawer.copied') : t('dailyStatus.drawer.copyMd')}
          </button>
          {canExportPdf && (
            <button
              type="button"
              onClick={downloadPdf}
              disabled={!report.data || busy !== null}
              className={btn}
            >
              {busy === 'pdf' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              {t('dailyStatus.drawer.pdf')}
            </button>
          )}
          {canSendEmail && (
            <button
              type="button"
              onClick={email}
              disabled={!report.data || busy !== null}
              className={btn}
            >
              {busy === 'email' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Mail size={14} />
              )}
              {t('dailyStatus.drawer.sendEmail')}
            </button>
          )}
        </div>
        {notice && (
          <p
            className={`px-4 pt-2 text-xs ${
              notice.tone === 'ok' ? 'text-emerald-600' : 'text-red-600'
            }`}
          >
            {notice.text}
          </p>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          {report.isLoading && <p className="mt-4 text-sm text-gray-500">…</p>}
          {report.isError && (
            <p className="mt-4 text-sm text-red-600">{t('dailyStatus.loadError')}</p>
          )}
          {report.data && (
            <>
              <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                {SECTION_ORDER.map((s) => (
                  <div key={s} className="rounded-md bg-gray-50 py-2">
                    <div className={`text-lg font-bold ${SECTION_STYLE[s].text}`}>
                      {report.data.summary[s]}
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500">
                      {t(`dailyStatus.section.${s}`)}
                    </div>
                  </div>
                ))}
              </div>
              {(report.data.summary.hoursToday > 0 || report.data.summary.unreported > 0) && (
                <p className="mt-2 text-xs text-gray-500">
                  {report.data.summary.hoursToday > 0 &&
                    t('dailyStatus.drawer.hoursToday', {
                      hours: report.data.summary.hoursToday,
                    })}
                  {report.data.summary.hoursToday > 0 &&
                    report.data.summary.unreported > 0 &&
                    ' · '}
                  {report.data.summary.unreported > 0 &&
                    t('dailyStatus.drawer.unreportedCount', {
                      count: report.data.summary.unreported,
                    })}
                </p>
              )}
              {report.data.summary.total === 0 ? (
                <p className="mt-4 text-sm text-gray-500">{t('dailyStatus.drawer.noTasks')}</p>
              ) : (
                <ReportSections sections={report.data.sections} />
              )}
            </>
          )}

          {history.data && history.data.length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                {t('dailyStatus.drawer.history')}
              </p>
              <ul className="mt-1 divide-y divide-gray-100">
                {history.data.map((item) => (
                  <li key={item.day}>
                    <button
                      type="button"
                      onClick={() => setDay(item.day)}
                      className={`flex w-full items-center justify-between py-1.5 text-left text-[13px] hover:bg-gray-50 ${
                        item.day === day ? 'font-semibold text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      <span>{item.day}</span>
                      <span className="text-[12px] text-gray-500">
                        ✅ {item.doneCount} · 🚫 {item.blockedCount} · {item.checkedIn}/
                        {item.totalTasks}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
